import React, { Suspense, lazy, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import Navbar from "./components/Navbar";
import MobileTabBar from "./components/MobileTabBar";
import BrandIcon from "./components/BrandIcon";
import useMobileViewport from "./hooks/useMobileViewport";

// Eagerly load only the entry route — everything else is split.
import HomePage from "./pages/HomePage";

// Route-level code splitting: each page becomes its own chunk and is fetched
// only when the user actually navigates there. Cuts initial JS payload.
const LoginPage             = lazy(() => import("./pages/LoginPage"));
const RegisterPage          = lazy(() => import("./pages/RegisterPage"));
const LoginSuccessPage      = lazy(() => import("./pages/LoginSuccessPage"));
const MyPage                = lazy(() => import("./pages/MyPage"));
const ForgotPasswordPage    = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage     = lazy(() => import("./pages/ResetPasswordPage"));
const VerifyEmailPage       = lazy(() => import("./pages/VerifyEmailPage"));
const HistoryPage           = lazy(() => import("./pages/HistoryPage"));
const PrivacyPolicyPage     = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsOfServicePage    = lazy(() => import("./pages/TermsOfServicePage"));
const DocumentAnalysisPage  = lazy(() => import("./pages/DocumentAnalysisPage"));
const PredictionPage        = lazy(() => import("./pages/PredictionPage"));
const DraftGeneratorPage    = lazy(() => import("./pages/DraftGeneratorPage"));
const AiToolsPage           = lazy(() => import("./pages/AiToolsPage"));
const AgentChatPage         = lazy(() => import("./pages/AgentChatPage"));
const AdminPage             = lazy(() => import("./pages/AdminPage"));
const DigitalTwinPage       = lazy(() => import("./pages/DigitalTwinPage"));

function ScrollablePage({ children }) {
  const scroller = useRef(null);
  const { pathname } = useLocation();
  useEffect(() => { scroller.current?.scrollTo(0, 0); }, [pathname]);
  return <div ref={scroller} className="app-page-scroll h-full overflow-y-auto">{children}</div>;
}

/* 인증 로딩 완료 전까지 빈 스플래시 */
function AuthGate({ children }) {
  const { loading } = useAuth();
  if (loading) {
    return (
      <div className="h-dvh flex items-center justify-center bg-[#f6f1e8]">
        <div className="flex flex-col items-center gap-3">
          <div className="h-12 w-12 rounded-2xl bg-[#1f2933] text-[#f6f1e8] flex items-center justify-center shadow-sm">
            <BrandIcon name="logo" size={26} title="마이로잇" />
          </div>
          <div className="w-6 h-6 border-2 border-[#9b6f45] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }
  return children;
}

// Subtle loader shown while route chunks are fetched.
function RouteFallback() {
  return (
    <div className="h-full flex items-center justify-center" aria-live="polite" aria-busy="true">
      <div className="w-6 h-6 border-2 border-[#9b6f45] border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function AppLayout() {
  const location = useLocation();
  const { height, keyboardOpen } = useMobileViewport();
  const isChatPage = location.pathname === '/' || location.pathname === '/agent' || location.pathname === '/digital-twin';

  return (
    <AuthGate>
        <div className={`app-shell flex flex-col bg-[#f6f1e8] text-[#1f2933]${keyboardOpen ? ' is-keyboard-open' : ''}`}
          style={height ? { '--app-height': `${height}px` } : undefined}>
          <Navbar />
          <main className="flex-1 min-h-0 min-w-0 overflow-hidden">
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/login" element={<ScrollablePage><LoginPage /></ScrollablePage>} />
                <Route path="/register" element={<ScrollablePage><RegisterPage /></ScrollablePage>} />
                <Route path="/login-success" element={<ScrollablePage><LoginSuccessPage /></ScrollablePage>} />
                <Route path="/mypage" element={<ScrollablePage><MyPage /></ScrollablePage>} />
                <Route path="/history" element={<ScrollablePage><HistoryPage /></ScrollablePage>} />
                <Route path="/forgot-password" element={<ScrollablePage><ForgotPasswordPage /></ScrollablePage>} />
                <Route path="/reset-password" element={<ScrollablePage><ResetPasswordPage /></ScrollablePage>} />
                <Route path="/verify-email" element={<ScrollablePage><VerifyEmailPage /></ScrollablePage>} />
                <Route path="/privacy" element={<ScrollablePage><PrivacyPolicyPage /></ScrollablePage>} />
                <Route path="/terms" element={<ScrollablePage><TermsOfServicePage /></ScrollablePage>} />
                <Route path="/document-analysis" element={<ScrollablePage><DocumentAnalysisPage /></ScrollablePage>} />
                <Route path="/prediction" element={<ScrollablePage><PredictionPage /></ScrollablePage>} />
                <Route path="/draft" element={<ScrollablePage><DraftGeneratorPage /></ScrollablePage>} />
                <Route path="/ai-tools" element={<ScrollablePage><AiToolsPage /></ScrollablePage>} />
                <Route path="/agent" element={<AgentChatPage />} />
                <Route path="/admin" element={<ScrollablePage><AdminPage /></ScrollablePage>} />
                <Route path="/digital-twin" element={<DigitalTwinPage />} />
              </Routes>
            </Suspense>
          </main>
          {/* B2B Footer — hidden on chat pages to maximize input area */}
          {!isChatPage && (
          <footer className="hidden lg:block bg-gray-50 border-t border-gray-200 py-4 px-4 text-center text-xs text-gray-500 shrink-0">
            <span className="font-semibold text-gray-600">B2B 비즈니스 문의</span>
            <span className="mx-1.5 text-gray-300">|</span>
            <span className="hover:text-gray-700 cursor-pointer" onClick={() => { const d = atob('MDUwMi02NjgxLTE5NzM='); window.location.href = 'tel:' + d; }}>{atob('MDUwMi02NjgxLTE5NzM=')}</span>
            <span className="mx-1.5 text-gray-300">|</span>
            <span className="hover:text-gray-700 cursor-pointer" onClick={() => { const d = atob('bWFzdGVyQHNrdGVjLmty'); window.location.href = 'mai' + 'lto:' + d; }}>{atob('bWFzdGVyQHNrdGVjLmty')}</span>
            <div className="mt-1 text-[11px] text-gray-400">&copy; 2026 Mobidic Company · myLawit</div>
          </footer>
          )}
          <MobileTabBar />
        </div>
    </AuthGate>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppLayout />
      </AuthProvider>
    </BrowserRouter>
  );
}
